import {FC, useCallback, useEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {useNavigate} from "react-router-dom";
import type {Menu} from "../../service/menu/types";
import type {AvatarPopupGroup} from "../../utils/homeShellMenuByPosition";
import {resolveMenuNavigationPath} from "../../utils/menuPathNormalize";
import {isMenuSvgIconCode, MenuSvgIcon} from "../../utils/menuIconRegistry";
import {
  getMembershipCurrentApi,
  type MembershipCurrentRespVO,
} from "../../service/membership";
import { globalStore } from "../../utils/globalStore";
import {
  ChevronRightMenuIcon,
  LogoutMenuIcon,
  SettingsMenuIcon,
} from "./sidebarMenuIcons";
import "./accountMenu.scss";

export interface AccountMenuPopupProps {
  /** 头像菜单是否打开（外部状态） */
  open: boolean;
  /** 头像菜单浮层锚定的元素（用户信息条） */
  anchorRef: React.RefObject<HTMLElement | null>;
  onClose: () => void;
  /** 来自菜单数据的后端 group（含嵌套 sub-group） */
  avatarPopupGroups: AvatarPopupGroup[];
  /** 头像菜单的 loose items：displayPosition='avatarPopup' 但不在任何 group 下的叶子 */
  looseAvatarPopupItems?: Menu.Menu[];
  /** 当前打开的 group key（null 表示二级 popup 关闭） */
  activeGroupKey: string | null;
  /** 点击 group toggle 时切换 */
  onToggleGroup: (key: string | null) => void;
  /** 点击叶子菜单时触发（路由 / 业务） */
  onItemClick: (menu: Menu.Menu) => void;
  onLogout: () => void;
}

/** 把日期字符串裁剪为 YYYY.MM.DD 显示 */
const formatDate = (value: string | undefined): string => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}.${m}.${day}`;
};

function buildMenuIconNode(menu: Menu.Menu): React.ReactNode {
  const icon = menu.meta?.icon?.trim();
  if (!icon || icon === "#" || icon === "") return null;
  if (isMenuSvgIconCode(icon)) return <MenuSvgIcon code={icon} size={15} />;
  let iconClass = icon;
  if (!iconClass.match(/^(fas|far|fal|fab)\s/)) {
    if (iconClass.startsWith("fa-")) iconClass = `fas ${iconClass}`;
    else if (!iconClass.includes("fa-")) iconClass = `fas fa-${iconClass}`;
  }
  return <i className={iconClass} />;
}

function groupKeyOf(g: AvatarPopupGroup): string {
  return `grp-${g.root.id ?? g.root.name}`;
}

export const AccountMenuPopup: FC<AccountMenuPopupProps> = ({
  open,
  anchorRef,
  onClose,
  avatarPopupGroups,
  looseAvatarPopupItems,
  activeGroupKey,
  onToggleGroup,
  onItemClick,
  onLogout,
}) => {
  const popup1Ref = useRef<HTMLDivElement | null>(null);
  const popup2Ref = useRef<HTMLDivElement | null>(null);
  const [anchorPos, setAnchorPos] = useState<{right: number; bottom: number}>({
    right: 0,
    bottom: 0,
  });
  const [membershipInfo, setMembershipInfo] = useState<MembershipCurrentRespVO | null>(
    () => globalStore.getMembership()
  );

  /** 每次打开时：若 globalStore 已有快照直接使用，否则异步拉取一次 */
  useEffect(() => {
    if (!open) return;
    const cached = globalStore.getMembership();
    if (cached) {
      // 已有快照，不重复请求，避免悬浮窗闪烁
      setMembershipInfo(cached);
      return;
    }
    let cancelled = false;
    setMembershipInfo(null);
    getMembershipCurrentApi()
      .then((res) => {
        if (cancelled) return;
        if (res?.code === 0 && res.data) {
          setMembershipInfo(res.data);
          globalStore.setMembership(res.data);
        }
      })
      .catch(() => {
        /* 静默失败：仅显示接口返回的数据 */
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  /**
   * 订阅 globalStore：业务侧在支付/购买/积分消耗后调用 triggerMembershipRefresh()，
   * HomeShell 会重新拉取并写回 globalStore.membership，本组件随之同步到最新数据。
   */
  useEffect(() => {
    const unsub = globalStore.subscribe(() => {
      const next = globalStore.getMembership();
      setMembershipInfo(next);
    });
    return unsub;
  }, []);

  /** 方案展示值：优先接口数据；用兜底文案锁定浮层高度，避免接口返回前/失败时大小突变 */
  const planLabel = membershipInfo?.planName || "未开通会员";
  const planExpiredAt = membershipInfo?.endTime
    ? formatDate(membershipInfo.endTime)
    : "—";

  const navigate = useNavigate();
  const handleUpgrade = useCallback(() => {
    onClose();
    navigate("/pricing");
  }, [navigate, onClose]);

  const positionAnchor = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    setAnchorPos({
      right: window.innerWidth - rect.right,
      bottom: window.innerHeight - rect.bottom,
    });
  }, [anchorRef]);

  useEffect(() => {
    positionAnchor();
    const onResize = () => positionAnchor();
    const onScroll = () => positionAnchor();
    window.addEventListener("resize", onResize, {passive: true});
    window.addEventListener("scroll", onScroll, true);
    const raf = window.requestAnimationFrame(positionAnchor);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll, true);
      window.cancelAnimationFrame(raf);
    };
  }, [positionAnchor]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const t = e.target instanceof Node ? e.target : null;
      if (!t) return;
      if (popup1Ref.current?.contains(t)) return;
      if (popup2Ref.current?.contains(t)) return;
      onClose();
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [open, onClose]);

  const handleItemClick = useCallback(
    (callback: () => void) => (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onClose();
      callback();
    },
    [onClose]
  );

  const renderLeafMenuItem = (item: Menu.Menu): React.ReactNode => {
    const label = item.meta?.title || item.name;
    const iconNode = buildMenuIconNode(item);
    const href = resolveMenuNavigationPath(
      (item.path || "").trim(),
      item.component
    );
    const isExternal = /^https?:\/\//i.test(href);
    if (isExternal) {
      return (
        <a
          key={item.id ?? item.name}
          className="account-menu-item"
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => {
            e.stopPropagation();
            onItemClick(item);
          }}
        >
          {iconNode}
          <span>{label}</span>
        </a>
      );
    }
    return (
      <button
        key={item.id ?? item.name}
        type="button"
        className="account-menu-item"
        onClick={(e) => {
          e.stopPropagation();
          onItemClick(item);
        }}
      >
        {iconNode}
        <span>{label}</span>
      </button>
    );
  };

  if (typeof document === "undefined") return null;

  const activeGroup =
    activeGroupKey !== null
      ? avatarPopupGroups.find((g) => groupKeyOf(g) === activeGroupKey) ?? null
      : null;

  const popup = (
    <div
      className="account-menu-anchor"
      style={{
        position: "fixed",
        right: anchorPos.right,
        bottom: anchorPos.bottom,
        width: 0,
        height: 0,
        pointerEvents: "none",
      }}
    >
      <div
        ref={popup1Ref}
        className={`account-menu-popup account-menu-popup--level-1${
          open ? " show" : ""
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="account-menu-plan">
          <div>
            <span>当前方案</span>
            <strong>{planLabel}</strong>
            <small>有效期至 {planExpiredAt}</small>
          </div>
          <a
            href="/pricing"
            onClick={(e) => {
              e.preventDefault();
              handleUpgrade();
            }}
          >
            升级
          </a>
        </div>

        {(looseAvatarPopupItems ?? []).map((item) => renderLeafMenuItem(item))}

        {avatarPopupGroups.map((g) => {
          const key = groupKeyOf(g);
          const hasChildren = g.items.length > 0;
          const isActive = activeGroupKey === key;
          const rootIconNode =
            buildMenuIconNode(g.root) ?? <SettingsMenuIcon />;
          return (
            <div
              key={key}
              className={`account-menu-group ${
                isActive ? "account-menu-group--active" : ""
              }`}
            >
              <button
                type="button"
                className="account-menu-item account-menu-toggle"
                onClick={(e) => {
                  e.stopPropagation();
                  if (hasChildren) {
                    onToggleGroup(isActive ? null : key);
                  } else {
                    onItemClick(g.root);
                  }
                }}
              >
                {rootIconNode}
                <span>{g.root.meta?.title || g.root.name}</span>
                {hasChildren ? <ChevronRightMenuIcon /> : null}
              </button>
            </div>
          );
        })}

        <div className="account-menu-divider" />

        <button
          type="button"
          className="account-menu-item account-menu-item--danger"
          onClick={handleItemClick(onLogout)}
        >
          <LogoutMenuIcon />
          <span>退出登录</span>
        </button>
      </div>

      {activeGroup && (
        <div
          ref={popup2Ref}
          className={`account-menu-popup account-menu-popup--level-2${
            open ? " show" : ""
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="account-menu-panel-title">
            {activeGroup.root.meta?.title || activeGroup.root.name}
          </div>
          {activeGroup.items.length === 0 ? (
            <div className="account-menu-empty">暂无子项</div>
          ) : (
            activeGroup.items.map((item) => renderLeafMenuItem(item))
          )}
        </div>
      )}
    </div>
  );

  return createPortal(popup, document.body);
};